"use client";

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ChevronDown } from "lucide-react";
import type { ConnectionConfig } from "../connection-studio";

interface HeadersTabProps {
  formData: ConnectionConfig;
  onChange: (updates: Partial<ConnectionConfig>) => void;
}

const COMMON_HEADERS = [
  { key: "Content-Type", value: "application/json" },
  { key: "Accept", value: "application/json" },
  { key: "Accept", value: "*/*" },
  { key: "User-Agent", value: "mHive/1.0" },
  { key: "X-Request-ID", value: "{{uuid}}" },
  { key: "Cache-Control", value: "no-cache" },
];

export function HeadersTab({ formData, onChange }: HeadersTabProps) {
  const [showPresets, setShowPresets] = useState(false);

  const headers = Object.entries(formData.headers || {});

  const addHeader = (key = "", value = "") => {
    const newHeaders = {
      ...formData.headers,
      [key || `Header-${headers.length + 1}`]: value,
    };
    onChange({ headers: newHeaders });
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...formData.headers };
    delete newHeaders[key];
    onChange({ headers: newHeaders });
  };

  const updateHeader = (oldKey: string, newKey: string, value: string) => {
    const newHeaders: Record<string, string> = {};
    Object.entries(formData.headers || {}).forEach(([k, v]) => {
      if (k === oldKey) {
        newHeaders[newKey] = value;
      } else {
        newHeaders[k] = v;
      }
    });
    onChange({ headers: newHeaders });
  };

  const addPreset = (header: { key: string; value: string }) => {
    const newHeaders = { ...formData.headers, [header.key]: header.value };
    onChange({ headers: newHeaders });
    setShowPresets(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-white">Custom Headers</h3>
          <p className="text-sm text-white/50">
            Add headers that will be sent with every request
          </p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPresets(!showPresets)}
              className="border-white/10 bg-white/5 text-white/70 hover:bg-white/10"
            >
              Presets
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
            {showPresets && (
              <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded-lg border border-white/10 bg-slate-900 p-2 shadow-xl">
                {COMMON_HEADERS.map((header, idx) => (
                  <button
                    key={idx}
                    onClick={() => addPreset(header)}
                    className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm text-white/80 hover:bg-white/10"
                  >
                    <span className="font-mono text-xs">{header.key}</span>
                    <span className="text-xs text-white/50">
                      {header.value}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => addHeader()}
            className="border-white/10 bg-white/5 text-white/70 hover:bg-white/10"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Header
          </Button>
        </div>
      </div>

      {/* Headers List */}
      <div className="space-y-3">
        {headers.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/10 p-8 text-center">
            <p className="text-white/50">No custom headers configured</p>
            <p className="mt-1 text-sm text-white/60">
              Click "Add Header" or select from presets to add headers
            </p>
          </div>
        ) : (
          headers.map(([key, value], index) => (
            <div key={index} className="flex gap-3">
              <div className="flex-1">
                <Input
                  value={key}
                  onChange={(e) => updateHeader(key, e.target.value, value)}
                  placeholder="Header name"
                  className="border-white/10 bg-white/5 font-mono text-sm text-white placeholder:text-white/40"
                />
              </div>
              <div className="flex-1">
                <Input
                  value={value}
                  onChange={(e) => updateHeader(key, key, e.target.value)}
                  placeholder="Header value"
                  className="border-white/10 bg-white/5 font-mono text-sm text-white placeholder:text-white/40"
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeHeader(key)}
                className="text-white/40 hover:text-rose-400"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Preview */}
      {headers.length > 0 && (
        <div className="space-y-2">
          <Label className="text-white/60">Preview</Label>
          <div className="rounded-lg border border-white/10 bg-slate-950 p-4 font-mono text-sm">
            {headers.map(([key, value]) => (
              <div key={key} className="text-white/80">
                <span className="text-emerald-400">{key}</span>
                <span className="text-white/40">: </span>
                <span className="text-white/60">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Variable Info */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-4">
        <h4 className="text-sm font-medium text-white/80">
          Available Variables
        </h4>
        <p className="mt-1 text-xs text-white/50">
          You can use these variables in header values:
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Badge
            variant="outline"
            className="border-white/10 bg-white/5 font-mono text-xs text-white/60"
          >
            {"{{uuid}}"}
          </Badge>
          <Badge
            variant="outline"
            className="border-white/10 bg-white/5 font-mono text-xs text-white/60"
          >
            {"{{timestamp}}"}
          </Badge>
          <Badge
            variant="outline"
            className="border-white/10 bg-white/5 font-mono text-xs text-white/60"
          >
            {"{{date}}"}
          </Badge>
        </div>
      </div>
    </div>
  );
}
